import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, EmptyState, FormCheckbox, FormSearchInput, StatusBadge } from '@/components/UI';
import ActionMenu, { type ActionMenuItem } from './ActionMenu';
import { getVariantLifecycleTone, VARIANT_LIFECYCLE_LABELS, type IdeaLabVariantSummary } from '../ideaLabUtils';
import { buildVariantTree, formatWallClockDate, variantMatchesQuery } from '../studyView';

/** Deeper lineage keeps indenting visually only this far, so long chains stay readable. */
const MAX_INDENT_DEPTH = 6;
const INDENT_REM = 1.25;

export interface StudyVariantActions {
    onEditRules: (variant: IdeaLabVariantSummary) => void;
    onFreeze: (variant: IdeaLabVariantSummary) => void;
    onMakeVariation: (variant: IdeaLabVariantSummary) => void;
    onSetControl: (variant: IdeaLabVariantSummary) => void;
    onArchive: (variant: IdeaLabVariantSummary) => void;
    isMakingVariation: boolean;
    isSettingControl: boolean;
}

interface StudyVariantsTableProps {
    variants: IdeaLabVariantSummary[];
    studyIsOpen: boolean;
    actions: StudyVariantActions;
    onAddVariant: () => void;
}

/**
 * The study's variants as a lineage tree: each variant sits under the one it was copied from, so a
 * family of variations reads top to bottom. Drafts keep their two next steps visible; everything else
 * lives in the row menu.
 */
const StudyVariantsTable = ({ variants, studyIsOpen, actions, onAddVariant }: StudyVariantsTableProps) => {
    const [query, setQuery] = useState('');
    const [showArchived, setShowArchived] = useState(false);
    const archivedCount = useMemo(() => variants.filter((variant) => variant.lifecycle === 'archived').length, [variants]);
    const variantsById = useMemo(() => new Map(variants.map((variant) => [variant.id, variant])), [variants]);
    const visibleVariants = useMemo(
        () => variants.filter((variant) => showArchived || variant.lifecycle !== 'archived'),
        [variants, showArchived]
    );
    const rows = useMemo(() => {
        const tree = buildVariantTree(visibleVariants);

        return query.trim() === ''
            ? tree
            : tree.filter((row) => variantMatchesQuery(row.variant, query)).map((row) => ({ ...row, depth: 0 }));
    }, [visibleVariants, query]);
    const isFiltering = query.trim() !== '';

    if (variants.length === 0) {
        return (
            <EmptyState
                action={
                    studyIsOpen ? (
                        <Button variant="primary" onClick={onAddVariant}>
                            Add the first variant
                        </Button>
                    ) : undefined
                }
                description="Bring in published entry rules, a saved draft, or copy another variant, then freeze it."
                size="sm"
                title="No variants yet"
            />
        );
    }

    const menuItems = (variant: IdeaLabVariantSummary): ActionMenuItem[] =>
        studyIsOpen && variant.lifecycle !== 'archived'
            ? [
                  {
                      label: 'Make a variation',
                      description: 'Copy into a new editable draft that keeps this variant as its parent',
                      onSelect: () => actions.onMakeVariation(variant),
                      disabled: actions.isMakingVariation
                  },
                  ...(variant.isStudyControl
                      ? []
                      : [
                            {
                                label: 'Set as control',
                                description: 'Make this the baseline other variants are compared against',
                                onSelect: () => actions.onSetControl(variant),
                                disabled: actions.isSettingControl
                            }
                        ]),
                  {
                      label: 'Archive',
                      description: 'Keep it readable but stop it joining new runs',
                      onSelect: () => actions.onArchive(variant),
                      tone: 'danger' as const
                  }
              ]
            : [];

    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <FormSearchInput
                    aria-label="Filter variants"
                    className="w-80"
                    fullWidth={false}
                    noMargin
                    placeholder="Filter by name, key, or #id"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                />
                {archivedCount > 0 && (
                    <FormCheckbox
                        checked={showArchived}
                        label={`Show archived (${archivedCount})`}
                        onChange={(event) => setShowArchived(event.target.checked)}
                    />
                )}
            </div>

            <div className="overflow-x-auto rounded-md border border-zinc-800 md:overflow-visible [&_th:first-child]:rounded-tl-md [&_th:last-child]:rounded-tr-md">
                <table className="w-full min-w-[820px] text-sm">
                    <thead>
                        <tr className="bg-[#131416] text-left text-[11px] uppercase tracking-wide text-zinc-400">
                            <th className="px-3 py-2 font-medium">Variant</th>
                            <th className="px-3 py-2 font-medium">Bars</th>
                            <th className="px-3 py-2 font-medium">State</th>
                            <th className="px-3 py-2 font-medium">Role</th>
                            <th className="px-3 py-2 font-medium">Frozen</th>
                            <th className="px-3 py-2">
                                <span className="sr-only">Actions</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 ? (
                            <tr>
                                <td className="px-3 py-6 text-center text-zinc-500" colSpan={6}>
                                    No variants match &ldquo;{query}&rdquo;.
                                </td>
                            </tr>
                        ) : (
                            rows.map(({ variant, depth }) => {
                                const parent =
                                    variant.parentVariantId === null ? null : (variantsById.get(variant.parentVariantId) ?? null);
                                const showParentHint = parent !== null && (isFiltering || depth === 0);
                                const isDraft = variant.lifecycle === 'draft';

                                return (
                                    <tr
                                        className={`border-t border-zinc-800 align-top hover:bg-zinc-800/30 ${
                                            variant.lifecycle === 'archived' ? 'opacity-60' : ''
                                        }`}
                                        data-variant-id={variant.id}
                                        key={variant.id}
                                    >
                                        <td className="px-3 py-2">
                                            <div
                                                className="flex min-w-0 gap-2"
                                                style={{ paddingLeft: `${Math.min(depth, MAX_INDENT_DEPTH) * INDENT_REM}rem` }}
                                            >
                                                {depth > 0 && (
                                                    <span aria-hidden="true" className="select-none text-zinc-600">
                                                        └
                                                    </span>
                                                )}
                                                <div className="min-w-0">
                                                    <Link
                                                        className="font-medium text-blue-300 hover:text-blue-200"
                                                        to={`/user/ideaLab/variant/${variant.id}`}
                                                    >
                                                        {variant.label}
                                                    </Link>
                                                    <div className="text-xs text-zinc-500">
                                                        {variant.variantKey} · #{variant.id}
                                                        {showParentHint && ` · child of ${parent.variantKey}`}
                                                    </div>
                                                    {variant.description && (
                                                        <p
                                                            className="mt-0.5 line-clamp-1 max-w-3xl text-xs text-zinc-400"
                                                            title={variant.description}
                                                        >
                                                            {variant.description}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-2 text-zinc-300">{variant.primaryDuration}</td>
                                        <td className="px-3 py-2">
                                            <StatusBadge status={variant.lifecycle} tone={getVariantLifecycleTone(variant.lifecycle)}>
                                                {VARIANT_LIFECYCLE_LABELS[variant.lifecycle]}
                                            </StatusBadge>
                                        </td>
                                        <td className="px-3 py-2">
                                            <span className="flex flex-wrap gap-1">
                                                {variant.isStudyControl && (
                                                    <StatusBadge status="control" tone="accent">
                                                        Control
                                                    </StatusBadge>
                                                )}
                                                {variant.promotionId !== null && (
                                                    <StatusBadge status="promoted" tone="success">
                                                        Promoted
                                                    </StatusBadge>
                                                )}
                                                {!variant.isStudyControl && variant.promotionId === null && (
                                                    <span className="text-xs text-zinc-600">—</span>
                                                )}
                                            </span>
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-2 text-xs text-zinc-400">
                                            {formatWallClockDate(variant.frozenAt)}
                                        </td>
                                        <td className="px-3 py-2">
                                            <div className="flex items-center justify-end gap-2">
                                                {studyIsOpen && isDraft && (
                                                    <>
                                                        <Button
                                                            size="sm"
                                                            variant="outline-dark"
                                                            onClick={() => actions.onEditRules(variant)}
                                                        >
                                                            Edit rules
                                                        </Button>
                                                        <Button size="sm" variant="secondary" onClick={() => actions.onFreeze(variant)}>
                                                            Freeze
                                                        </Button>
                                                    </>
                                                )}
                                                <ActionMenu items={menuItems(variant)} label={`Actions for ${variant.variantKey}`} />
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
            <p className="text-xs text-zinc-500">
                Each variant sits under the variant it was copied from. Freezing compiles a draft&apos;s rules into an immutable artifact
                that runs evaluate.
            </p>
        </div>
    );
};

export default StudyVariantsTable;
